/** @file parseNumberInput: what a typed field text means (absolute, relative or formula) for a value. */
import { evaluateArithmetic } from './evaluateArithmetic';

const RELATIVE = /^([-+*/])(=?)([\s\S]*)$/;

const APPLY: Record<string, (current: number, operand: number) => number> = {
  '+': (c, o) => c + o,
  '-': (c, o) => c - o,
  '*': (c, o) => c * o,
  '/': (c, o) => (o === 0 ? Number.NaN : c / o),
};

/** Trims binary float noise (0.1 + 0.2) without cutting a precision the user typed; null stays null, non-finite is null. */
function tidy(n: number | null): number | null {
  return n !== null && Number.isFinite(n) ? Number(n.toPrecision(12)) : null;
}

/**
 * `5` is 5 and `-5` is -5 (a leading minus is a sign); a leading `+`, `*` or `/` is relative to `current` (`+5`, `+-5`,
 * `*2`, `/2`), and `+=`, `-=`, `*=`, `/=` are the same with an explicit `=`; anything else is a formula of
 * `+ - * / ( )` and decimals. Null = rejected (not arithmetic, divides by zero, overflows): the field keeps its value.
 */
export function parseNumberInput(text: string, current: number): number | null {
  const t = text.trim();
  const [, op = '', eq = '', rest = ''] = RELATIVE.exec(t) ?? [];
  const apply = APPLY[op];
  if (apply === undefined || (op === '-' && eq === '')) return tidy(evaluateArithmetic(t));
  const operand = evaluateArithmetic(rest);
  return operand === null ? null : tidy(apply(current, operand));
}
